import React, { useCallback, useState } from "react";
import { Contact, ContactField } from "../types/cardToCrm.types";
import { extractBusinessCards } from "../businessCardService";
import { filesToCompressedDataUrls } from "../utils/imageToBase64";
import { contactsToCsv, downloadCsv } from "../utils/csv";
import { debug } from "../../../utils/debugLogger";
import BusinessCardUploader from "./BusinessCardUploader";
import ContactCard from "./ContactCard";
import UpsellBanner from "./UpsellBanner";

/**
 * Public, anonymous Card-to-CRM tool (Phase 1).
 * Upload business-card photos → AI extracts contacts → edit → download CSV.
 * Contacts live in React state only; nothing is persisted server-side.
 */
const CardToCrmPage: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(async (files: File[]) => {
    setError(null);
    setExtracting(true);
    setPendingCount(files.length);
    try {
      const images = await filesToCompressedDataUrls(files);
      const extracted = await extractBusinessCards(images);
      if (extracted.length === 0) {
        setError(
          "No contact details were found. Try a sharper, well-lit photo of the card."
        );
      }
      setContacts((prev) => [...prev, ...extracted]);
    } catch (e) {
      debug.error("Extraction failed:", e);
      setError(
        e instanceof Error ? e.message : "Something went wrong. Please try again."
      );
    } finally {
      setExtracting(false);
      setPendingCount(0);
    }
  }, []);

  const handleFieldChange = useCallback(
    (id: string, field: ContactField, value: string) => {
      setContacts((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, [field]: value === "" ? null : value } : c
        )
      );
    },
    []
  );

  const handleDelete = useCallback((id: string) => {
    setContacts((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleDownload = useCallback(() => {
    const csv = contactsToCsv(contacts);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`business-cards-${stamp}.csv`, csv);
  }, [contacts]);

  const hasContacts = contacts.length > 0;

  return (
    <div className="min-h-screen bg-sagelyn-background text-sagelyn-foreground">
      <div className="mx-auto max-w-4xl px-4 py-10 sm:py-14">
        {/* Header */}
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Business Card Scanner
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sagelyn-muted">
            Snap or upload photos of business cards and instantly turn them into
            a clean contact list. Free, no signup required.
          </p>
        </header>

        {/* Uploader */}
        {!hasContacts && (
          <BusinessCardUploader onFilesSelected={handleFiles} busy={extracting} />
        )}

        {/* Extraction progress */}
        {extracting && (
          <div className="mt-6 flex items-center justify-center gap-3 rounded-xl border border-sagelyn-border bg-sagelyn-surface px-4 py-3 text-sm text-sagelyn-muted">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-sagelyn-primary border-t-transparent" />
            Reading {pendingCount} {pendingCount === 1 ? "card" : "cards"}…
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Results */}
        {hasContacts && (
          <section className="mt-8">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">
                {contacts.length}{" "}
                {contacts.length === 1 ? "contact" : "contacts"}
              </h2>
              <div className="flex items-center gap-3">
                <BusinessCardUploader
                  onFilesSelected={handleFiles}
                  busy={extracting}
                  compact
                />
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 rounded-lg bg-sagelyn-primary px-4 py-2 text-sm font-semibold text-sagelyn-charcoal transition-colors hover:bg-sagelyn-secondary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v12m0 0 4-4m-4 4-4-4M4 18h16"
                    />
                  </svg>
                  Download CSV
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {contacts.map((contact, index) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  index={index}
                  onChange={handleFieldChange}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            <UpsellBanner />
          </section>
        )}
      </div>
    </div>
  );
};

export default CardToCrmPage;
